import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { persist, createJSONStorage } from 'zustand/middleware';

// Types for undo/redo
interface CellChange {
  type: 'cell';
  taskId: string;
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

interface TaskCreateChange {
  type: 'task_create';
  taskId: string;
  task: unknown;
}

interface TaskDeleteChange {
  type: 'task_delete';
  taskId: string;
  task: unknown;
  index: number;
}

interface ColumnChange {
  type: 'column';
  columnId: string;
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

interface BatchChange {
  type: 'batch';
  changes: Change[];
}

interface DependencyChange {
  type: 'dependency';
  action: 'create' | 'delete' | 'update';
  dependencyId: string;
  data: unknown;
}

type Change =
  | CellChange
  | TaskCreateChange
  | TaskDeleteChange
  | ColumnChange
  | BatchChange
  | DependencyChange;

interface HistoryEntry {
  changes: Change[];
  timestamp: number;
  description?: string;
}

// Selection state
interface CellPosition {
  rowIndex: number;
  columnIndex: number;
}

interface CellRange {
  start: CellPosition;
  end: CellPosition;
}

interface SortConfig {
  columnId: string;
  direction: 'asc' | 'desc';
}

interface FilterConfig {
  columnId: string;
  operator: 'equals' | 'contains' | 'startsWith' | 'endsWith' | 'gt' | 'lt' | 'gte' | 'lte' | 'between' | 'isEmpty' | 'isNotEmpty';
  value: unknown;
  value2?: unknown; // For 'between' operator
}

interface GridState {
  // Selection
  selectedCell: CellPosition | null;
  selectedCells: CellRange | null;
  editingCell: { taskId: string; columnId: string } | null;
  selectedRows: Set<string>;

  // Clipboard
  clipboard: {
    type: 'cells' | 'rows';
    data: unknown;
    sourceRange?: CellRange;
  } | null;
  copiedCells: Map<string, unknown>; // taskId:columnId -> value

  // History for undo/redo
  undoStack: HistoryEntry[];
  redoStack: HistoryEntry[];
  maxHistorySize: number;

  // Column state
  columnWidths: Map<string, number>;
  columnOrder: string[];
  hiddenColumns: Set<string>;
  frozenColumns: number;

  // Row state
  expandedRows: Set<string>;
  rowHeights: Map<string, number>;

  // Sorting and filtering
  sortConfig: SortConfig[];
  filters: FilterConfig[];

  // UI state
  isLoading: boolean;
  error: string | null;
  isDragging: boolean;
  draggedRowId: string | null;
  hoverRowId: string | null;
  contextMenuPosition: { x: number; y: number } | null;

  // Active sheet context
  activeSheetId: string | null;

  // Actions - Selection
  selectCell: (position: CellPosition | null) => void;
  selectCellRange: (range: CellRange | null) => void;
  extendSelection: (position: CellPosition) => void;
  startEditing: (taskId: string, columnId: string) => void;
  stopEditing: () => void;
  selectRow: (taskId: string, selected: boolean, exclusive?: boolean) => void;
  selectRowRange: (startId: string, endId: string, taskIds: string[]) => void;
  selectAllRows: (taskIds: string[]) => void;
  clearSelection: () => void;

  // Actions - Navigation
  moveSelection: (direction: 'up' | 'down' | 'left' | 'right', totalRows: number, totalCols: number) => void;
  moveTo: (position: CellPosition) => void;

  // Actions - Clipboard
  copy: (data: unknown, type: 'cells' | 'rows', sourceRange?: CellRange) => void;
  copyCell: (taskId: string, columnId: string, value: unknown) => void;
  paste: () => { type: 'cells' | 'rows'; data: unknown; sourceRange?: CellRange } | null;
  clearClipboard: () => void;

  // Actions - History (Undo/Redo)
  pushChange: (changes: Change[], description?: string) => void;
  undo: () => Change[] | null;
  redo: () => Change[] | null;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clearHistory: () => void;

  // Actions - Columns
  setColumnWidth: (columnId: string, width: number) => void;
  setColumnOrder: (order: string[]) => void;
  toggleColumnVisibility: (columnId: string) => void;
  setFrozenColumns: (count: number) => void;

  // Actions - Rows
  toggleRowExpansion: (taskId: string) => void;
  expandAll: () => void;
  collapseAll: () => void;
  setRowHeight: (taskId: string, height: number) => void;

  // Actions - Sorting and Filtering
  setSortConfig: (config: SortConfig[]) => void;
  addSort: (columnId: string, direction: 'asc' | 'desc') => void;
  removeSort: (columnId: string) => void;
  clearSort: () => void;
  setFilters: (filters: FilterConfig[]) => void;
  addFilter: (filter: FilterConfig) => void;
  removeFilter: (columnId: string) => void;
  clearFilters: () => void;

  // Actions - UI
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setDragging: (isDragging: boolean, rowId?: string) => void;
  setHoverRow: (rowId: string | null) => void;
  setContextMenu: (position: { x: number; y: number } | null) => void;
  setActiveSheet: (sheetId: string | null) => void;

  // Actions - Reset
  resetGridState: () => void;
}

const initialState = {
  selectedCell: null,
  selectedCells: null,
  editingCell: null,
  selectedRows: new Set<string>(),
  clipboard: null,
  copiedCells: new Map<string, unknown>(),
  undoStack: [],
  redoStack: [],
  maxHistorySize: 50,
  columnWidths: new Map<string, number>(),
  columnOrder: [],
  hiddenColumns: new Set<string>(),
  frozenColumns: 0,
  expandedRows: new Set<string>(),
  rowHeights: new Map<string, number>(),
  sortConfig: [],
  filters: [],
  isLoading: false,
  error: null,
  isDragging: false,
  draggedRowId: null,
  hoverRowId: null,
  contextMenuPosition: null,
  activeSheetId: null,
};

export const useGridStore = create<GridState>()(
  persist(
    immer((set, get) => ({
      ...initialState,

      // Selection actions
      selectCell: (position) => {
        set((state) => {
          state.selectedCell = position;
          state.selectedCells = null;
          if (position && state.editingCell) {
            state.editingCell = null;
          }
        });
      },

      selectCellRange: (range) => {
        set((state) => {
          state.selectedCells = range;
          state.selectedCell = range?.start ?? null;
        });
      },

      extendSelection: (position) => {
        set((state) => {
          if (state.selectedCell) {
            state.selectedCells = {
              start: state.selectedCell,
              end: position,
            };
          }
        });
      },

      startEditing: (taskId, columnId) => {
        set((state) => {
          state.editingCell = { taskId, columnId };
        });
      },

      stopEditing: () => {
        set((state) => {
          state.editingCell = null;
        });
      },

      selectRow: (taskId, selected, exclusive = false) => {
        set((state) => {
          if (exclusive) {
            state.selectedRows = new Set(selected ? [taskId] : []);
          } else if (selected) {
            state.selectedRows.add(taskId);
          } else {
            state.selectedRows.delete(taskId);
          }
        });
      },

      selectRowRange: (startId, endId, taskIds) => {
        set((state) => {
          const startIndex = taskIds.indexOf(startId);
          const endIndex = taskIds.indexOf(endId);
          if (startIndex === -1 || endIndex === -1) return;

          const minIndex = Math.min(startIndex, endIndex);
          const maxIndex = Math.max(startIndex, endIndex);

          for (let i = minIndex; i <= maxIndex; i++) {
            state.selectedRows.add(taskIds[i]);
          }
        });
      },

      selectAllRows: (taskIds) => {
        set((state) => {
          state.selectedRows = new Set(taskIds);
        });
      },

      clearSelection: () => {
        set((state) => {
          state.selectedCell = null;
          state.selectedCells = null;
          state.editingCell = null;
          state.selectedRows = new Set();
        });
      },

      // Navigation actions
      moveSelection: (direction, totalRows, totalCols) => {
        set((state) => {
          if (!state.selectedCell) {
            state.selectedCell = { rowIndex: 0, columnIndex: 0 };
            return;
          }

          const { rowIndex, columnIndex } = state.selectedCell;
          let newRow = rowIndex;
          let newCol = columnIndex;

          switch (direction) {
            case 'up':
              newRow = Math.max(0, rowIndex - 1);
              break;
            case 'down':
              newRow = Math.min(totalRows - 1, rowIndex + 1);
              break;
            case 'left':
              newCol = Math.max(0, columnIndex - 1);
              break;
            case 'right':
              newCol = Math.min(totalCols - 1, columnIndex + 1);
              break;
          }

          state.selectedCell = { rowIndex: newRow, columnIndex: newCol };
          state.selectedCells = null;
          state.editingCell = null;
        });
      },

      moveTo: (position) => {
        set((state) => {
          state.selectedCell = position;
          state.selectedCells = null;
        });
      },

      // Clipboard actions
      copy: (data, type, sourceRange) => {
        set((state) => {
          state.clipboard = { type, data, sourceRange };
        });
      },

      copyCell: (taskId, columnId, value) => {
        set((state) => {
          state.copiedCells.set(`${taskId}:${columnId}`, value);
        });
      },

      paste: () => {
        return get().clipboard;
      },

      clearClipboard: () => {
        set((state) => {
          state.clipboard = null;
          state.copiedCells.clear();
        });
      },

      // History actions
      pushChange: (changes, description) => {
        set((state) => {
          // Clear redo stack when new change is made
          state.redoStack = [];

          // Add to undo stack
          state.undoStack.push({
            changes,
            timestamp: Date.now(),
            description,
          });

          // Trim to max size
          if (state.undoStack.length > state.maxHistorySize) {
            state.undoStack.shift();
          }
        });
      },

      undo: () => {
        const currentState = get();
        if (currentState.undoStack.length === 0) return null;

        const entry = currentState.undoStack[currentState.undoStack.length - 1];
        set((state) => {
          const undone = state.undoStack.pop();
          if (undone) {
            state.redoStack.push(undone);
          }
        });

        return entry?.changes ?? null;
      },

      redo: () => {
        const currentState = get();
        if (currentState.redoStack.length === 0) return null;

        const entry = currentState.redoStack[currentState.redoStack.length - 1];
        set((state) => {
          const redone = state.redoStack.pop();
          if (redone) {
            state.undoStack.push(redone);
          }
        });

        return entry?.changes ?? null;
      },

      canUndo: () => {
        return get().undoStack.length > 0;
      },

      canRedo: () => {
        return get().redoStack.length > 0;
      },

      clearHistory: () => {
        set((state) => {
          state.undoStack = [];
          state.redoStack = [];
        });
      },

      // Column actions
      setColumnWidth: (columnId, width) => {
        set((state) => {
          state.columnWidths.set(columnId, width);
        });
      },

      setColumnOrder: (order) => {
        set((state) => {
          state.columnOrder = order;
        });
      },

      toggleColumnVisibility: (columnId) => {
        set((state) => {
          if (state.hiddenColumns.has(columnId)) {
            state.hiddenColumns.delete(columnId);
          } else {
            state.hiddenColumns.add(columnId);
          }
        });
      },

      setFrozenColumns: (count) => {
        set((state) => {
          state.frozenColumns = count;
        });
      },

      // Row actions
      toggleRowExpansion: (taskId) => {
        set((state) => {
          if (state.expandedRows.has(taskId)) {
            state.expandedRows.delete(taskId);
          } else {
            state.expandedRows.add(taskId);
          }
        });
      },

      expandAll: () => {
        // This is a signal - the component will handle populating expandedRows
        // with all summary task IDs based on task data it has access to
      },

      collapseAll: () => {
        set((state) => {
          state.expandedRows = new Set();
        });
      },

      setRowHeight: (taskId, height) => {
        set((state) => {
          state.rowHeights.set(taskId, height);
        });
      },

      // Sorting actions
      setSortConfig: (config) => {
        set((state) => {
          state.sortConfig = config;
        });
      },

      addSort: (columnId: string, direction: 'asc' | 'desc') => {
        set((state) => {
          const existing = state.sortConfig.findIndex(
            (s: SortConfig) => s.columnId === columnId
          );
          if (existing !== -1) {
            state.sortConfig[existing].direction = direction;
          } else {
            state.sortConfig.push({ columnId, direction });
          }
        });
      },

      removeSort: (columnId: string) => {
        set((state) => {
          state.sortConfig = state.sortConfig.filter(
            (s: SortConfig) => s.columnId !== columnId
          );
        });
      },

      clearSort: () => {
        set((state) => {
          state.sortConfig = [];
        });
      },

      // Filter actions
      setFilters: (filters) => {
        set((state) => {
          state.filters = filters;
        });
      },

      addFilter: (filter: FilterConfig) => {
        set((state) => {
          const existing = state.filters.findIndex(
            (f: FilterConfig) => f.columnId === filter.columnId
          );
          if (existing !== -1) {
            state.filters[existing] = filter;
          } else {
            state.filters.push(filter);
          }
        });
      },

      removeFilter: (columnId: string) => {
        set((state) => {
          state.filters = state.filters.filter((f: FilterConfig) => f.columnId !== columnId);
        });
      },

      clearFilters: () => {
        set((state) => {
          state.filters = [];
        });
      },

      // UI actions
      setLoading: (loading) => {
        set((state) => {
          state.isLoading = loading;
        });
      },

      setError: (error) => {
        set((state) => {
          state.error = error;
        });
      },

      setDragging: (isDragging, rowId) => {
        set((state) => {
          state.isDragging = isDragging;
          state.draggedRowId = isDragging ? rowId ?? null : null;
        });
      },

      setHoverRow: (rowId) => {
        set((state) => {
          state.hoverRowId = rowId;
        });
      },

      setContextMenu: (position) => {
        set((state) => {
          state.contextMenuPosition = position;
        });
      },

      setActiveSheet: (sheetId) => {
        set((state) => {
          if (state.activeSheetId !== sheetId) {
            // Reset selection and expansion when changing sheets
            state.selectedCell = null;
            state.selectedCells = null;
            state.editingCell = null;
            state.selectedRows = new Set();
            state.expandedRows = new Set();
          }
          state.activeSheetId = sheetId;
        });
      },

      // Reset
      resetGridState: () => {
        set((state) => {
          Object.assign(state, initialState);
          state.selectedRows = new Set();
          state.copiedCells = new Map();
          state.columnWidths = new Map();
          state.hiddenColumns = new Set();
          state.expandedRows = new Set();
          state.rowHeights = new Map();
        });
      },
    })),
    {
      name: 'projectflow-grid-state',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist certain state to localStorage
        columnWidths: Object.fromEntries(state.columnWidths),
        hiddenColumns: Array.from(state.hiddenColumns),
        frozenColumns: state.frozenColumns,
        expandedRows: Array.from(state.expandedRows),
      }),
      onRehydrateStorage: () => (state) => {
        // Convert persisted objects back to Maps/Sets
        if (state) {
          state.columnWidths = new Map(Object.entries(state.columnWidths || {}));
          state.hiddenColumns = new Set(state.hiddenColumns || []);
          state.expandedRows = new Set(state.expandedRows || []);
        }
      },
    }
  )
);

// Selectors
export const selectSelectedCell = (state: GridState) => state.selectedCell;
export const selectSelectedCells = (state: GridState) => state.selectedCells;
export const selectEditingCell = (state: GridState) => state.editingCell;
export const selectSelectedRows = (state: GridState) => state.selectedRows;
export const selectCanUndo = (state: GridState) => state.undoStack.length > 0;
export const selectCanRedo = (state: GridState) => state.redoStack.length > 0;
export const selectSortConfig = (state: GridState) => state.sortConfig;
export const selectFilters = (state: GridState) => state.filters;
export const selectExpandedRows = (state: GridState) => state.expandedRows;
export const selectColumnWidths = (state: GridState) => state.columnWidths;
export const selectHiddenColumns = (state: GridState) => state.hiddenColumns;
export const selectActiveSheet = (state: GridState) => state.activeSheetId;

// Helper to check if a cell is in the selected range
export const isCellInRange = (
  position: CellPosition,
  range: CellRange | null
): boolean => {
  if (!range) return false;

  const minRow = Math.min(range.start.rowIndex, range.end.rowIndex);
  const maxRow = Math.max(range.start.rowIndex, range.end.rowIndex);
  const minCol = Math.min(range.start.columnIndex, range.end.columnIndex);
  const maxCol = Math.max(range.start.columnIndex, range.end.columnIndex);

  return (
    position.rowIndex >= minRow &&
    position.rowIndex <= maxRow &&
    position.columnIndex >= minCol &&
    position.columnIndex <= maxCol
  );
};

// Export types
export type {
  GridState,
  CellPosition,
  CellRange,
  SortConfig,
  FilterConfig,
  Change,
  CellChange,
  TaskCreateChange,
  TaskDeleteChange,
  ColumnChange,
  BatchChange,
  DependencyChange,
  HistoryEntry,
};
